import React from "react";

export const MAX_CHAUFFEURS = 5;

export const emptyChauffeur = () => ({ name: "", phone: "", vehicle: "" });
export const emptyTrip = () => ({ pickup: "", when: "", pax: "", stops: [], dropoff: "", amount: "", description: "", chauffeurs: [] });

export const tripTotalCents = (t) => {
  const n = Number(t.amount);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
};
export const tripComplete = (t) =>
  !!(t.pickup.trim() && t.dropoff.trim() && t.when.trim() && Number(t.pax) > 0 && tripTotalCents(t) > 0 && Number(t.amount) <= 5000 && t.description.trim());

/** One trip inside a Quick Charge: route + stops, its chauffeurs (0..5), amount and "what for". */
export function QuickTripCard({ index, trip, onChange, onRemove, canRemove }) {
  const i = index;
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  const setStop = (si, k, v) => onChange({ stops: trip.stops.map((s, idx) => idx === si ? { ...s, [k]: v } : s) });
  const setChauf = (ci, k, v) => onChange({ chauffeurs: trip.chauffeurs.map((c, idx) => idx === ci ? { ...c, [k]: v } : c) });
  const addChauf = () => { if (trip.chauffeurs.length < MAX_CHAUFFEURS) onChange({ chauffeurs: [...trip.chauffeurs, emptyChauffeur()] }); };
  const complete = tripComplete(trip);

  return (
    <div className={`border ${complete ? "border-[#D4AF37]/30" : "border-white/10"} bg-black/40 p-4`} data-testid={`qc-trip-${i}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Trip {i + 1}</span>
        <span className="text-[10px] text-[#71717A]">{complete ? "complete" : "fill in every required field"}</span>
        {canRemove && (
          <button data-testid={`qc-trip-remove-${i}`} onClick={onRemove} className="ml-auto text-[10px] uppercase tracking-[0.15em] text-[#71717A] hover:text-red-400">Remove trip</button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="field-label">Pickup address <span className="text-red-400">*</span></label>
          <input data-testid={`qc-pickup-${i}`} className="field-input" placeholder="e.g. MKE Airport, Milwaukee, WI" value={trip.pickup} onChange={set("pickup")}/>
        </div>
        <div>
          <label className="field-label">Pickup date &amp; time <span className="text-red-400">*</span></label>
          <input data-testid={`qc-when-${i}`} type="datetime-local" className="field-input [color-scheme:dark]" value={trip.when} onChange={set("when")}/>
        </div>
        <div>
          <label className="field-label">Passengers <span className="text-red-400">*</span></label>
          <input data-testid={`qc-pax-${i}`} type="number" min="1" className="field-input" placeholder="2" value={trip.pax} onChange={set("pax")}/>
        </div>
      </div>
      {trip.stops.map((st, si) => (
        <div key={si} className="flex gap-2 items-center mt-3">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] w-14 shrink-0">Stop {si + 1}</span>
          <input data-testid={`qc-stop-${i}-${si}`} className="field-input flex-1" placeholder="e.g. Downtown, Milwaukee, WI" value={st.address} onChange={e => setStop(si, "address", e.target.value)}/>
          <input data-testid={`qc-stop-time-${i}-${si}`} type="time" className="field-input [color-scheme:dark]" style={{ width: "8rem", flex: "0 0 auto" }} title="Pickup time at this stop (optional)" value={st.time} onChange={e => setStop(si, "time", e.target.value)}/>
          <button data-testid={`qc-stop-remove-${i}-${si}`} onClick={() => onChange({ stops: trip.stops.filter((_, idx) => idx !== si) })} className="text-[#71717A] hover:text-red-400 p-1.5">✕</button>
        </div>
      ))}
      <button data-testid={`qc-add-stop-${i}`} onClick={() => onChange({ stops: [...trip.stops, { address: "", time: "" }] })} className="btn-ghost !py-2 !px-3 text-[10px] mt-3">+ Add stop</button>
      <div className="mt-3">
        <label className="field-label">Drop-off address <span className="text-red-400">*</span></label>
        <input data-testid={`qc-dropoff-${i}`} className="field-input" placeholder="e.g. Nashotah, WI" value={trip.dropoff} onChange={set("dropoff")}/>
      </div>

      {/* Chauffeurs for this trip (0..5) */}
      <div className="mt-4 pt-3 border-t border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Chauffeur{trip.chauffeurs.length !== 1 ? "s" : ""}</span>
          <span className="text-[10px] text-[#71717A]">optional — shown under this trip on the invoice · {trip.chauffeurs.length}/{MAX_CHAUFFEURS}</span>
        </div>
        {trip.chauffeurs.map((c, ci) => (
          <div key={ci} className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 items-end mb-2" data-testid={`qc-chauffeur-${i}-${ci}`}>
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] pb-2.5 md:w-8">#{ci + 1}</span>
            <div>
              {ci === 0 && <label className="field-label">Chauffeur name</label>}
              <input data-testid={`qc-ch-name-${i}-${ci}`} className="field-input" placeholder="e.g. Marcus" value={c.name} onChange={e => setChauf(ci, "name", e.target.value)}/>
            </div>
            <div>
              {ci === 0 && <label className="field-label">Chauffeur phone</label>}
              <input data-testid={`qc-ch-phone-${i}-${ci}`} className="field-input" placeholder="(414) 555-0100" value={c.phone} onChange={e => setChauf(ci, "phone", e.target.value)}/>
            </div>
            <div>
              {ci === 0 && <label className="field-label">Vehicle</label>}
              <input data-testid={`qc-ch-vehicle-${i}-${ci}`} className="field-input" placeholder="e.g. Black Suburban" value={c.vehicle} onChange={e => setChauf(ci, "vehicle", e.target.value)}/>
            </div>
            <button data-testid={`qc-ch-remove-${i}-${ci}`} onClick={() => onChange({ chauffeurs: trip.chauffeurs.filter((_, idx) => idx !== ci) })} className="text-[#71717A] hover:text-red-400 p-1.5 pb-2.5">✕</button>
          </div>
        ))}
        <button data-testid={`qc-add-chauffeur-${i}`} onClick={addChauf} disabled={trip.chauffeurs.length >= MAX_CHAUFFEURS}
          className="btn-ghost !py-2 !px-3 text-[10px] disabled:opacity-40" title={trip.chauffeurs.length >= MAX_CHAUFFEURS ? `Maximum ${MAX_CHAUFFEURS} chauffeurs per trip` : "Assign another chauffeur to this trip"}>
          + Add chauffeur
        </button>
      </div>

      {/* This trip's amount + what for */}
      <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="field-label">Amount ($) <span className="text-red-400">*</span></label>
          <input data-testid={`qc-amount-${i}`} type="number" min="0.01" max="5000" step="0.01" className="field-input" placeholder="120.00" value={trip.amount} onChange={set("amount")}/>
        </div>
        <div className="md:col-span-2">
          <label className="field-label">What is this charge for? <span className="text-red-400">*</span></label>
          <input data-testid={`qc-description-${i}`} className="field-input" placeholder="e.g. Ride 8/16 — MKE Airport to Downtown (no reservation)" value={trip.description} onChange={set("description")}/>
        </div>
      </div>
    </div>
  );
}
